import { Box, Collapse, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { UnitOfMeasure } from "@nens/rana/api-v1";
import { RanaAlert, RanaForm, RanaModal, RanaSelect, SubmitFunction } from "@nens/ranalib";

const CUSTOM_VALUE = "__custom__";

type CustomUnitOfMeasure = {
  code: string;
  label?: string | null;
  symbol?: string | null;
};

type RasterUnitPickerProps = {
  rasterUnit: UnitOfMeasure | undefined | null;
  setRasterUnit: ((unit: UnitOfMeasure | undefined) => void) | undefined;
  defaultUnits: UnitOfMeasure[];
};

export const RasterUnitPicker = ({
  defaultUnits,
  rasterUnit,
  setRasterUnit,
}: RasterUnitPickerProps) => {
  const isReadOnly = !setRasterUnit;
  const intl = useIntl();
  const {
    isOpen: isCustomModalOpen,
    onOpen: openCustomModal,
    onClose: closeCustomModal,
  } = useDisclosure();
  const currentCode = rasterUnit?.code != null ? String(rasterUnit.code) : null;
  const isCurrentInDefaults = defaultUnits?.some((u) => String(u.code) === currentCode);
  const hasCustomRaterUnit = rasterUnit && !isCurrentInDefaults;

  const options = [
    // Surface a custom (non-default) current unit so it remains selectable
    ...(hasCustomRaterUnit
      ? [
          {
            label: `${rasterUnit.label ?? rasterUnit.symbol ?? currentCode} (${intl.formatMessage({
              id: "file.raster.custom",
              defaultMessage: "custom",
            })})`,
            value: currentCode ?? "",
          },
        ]
      : [
          {
            label: `-- ${intl.formatMessage({
              id: "file.raster.custom_unit",
              defaultMessage: "Custom unit",
            })} --`,
            value: CUSTOM_VALUE,
          },
        ]),
    ...(defaultUnits ?? []).map((u) => ({
      label: u.label ?? u.symbol ?? String(u.code),
      value: String(u.code),
    })),
  ];

  const handleChange = (value: string | null) => {
    if (value === CUSTOM_VALUE || (!isCurrentInDefaults && value === currentCode)) {
      openCustomModal();
      return;
    }
    const unit = defaultUnits?.find((u) => String(u.code) === value);
    if (unit) setRasterUnit?.(unit);
  };

  const applyCustomUnit: SubmitFunction<CustomUnitOfMeasure> = ({ code, label, symbol }) => {
    setRasterUnit?.({
      code: code as UnitOfMeasure["code"],
      label,
      symbol,
    });

    closeCustomModal();
  };

  const intialValues: CustomUnitOfMeasure = hasCustomRaterUnit
    ? { ...rasterUnit, code: String(rasterUnit.code) }
    : { code: "", label: "", symbol: "" };

  return (
    <>
      <Box position="relative">
        <RanaSelect
          isReadOnly={isReadOnly}
          w="full"
          options={options}
          value={currentCode}
          onChange={handleChange}
        />
        <Collapse in={!isReadOnly} unmountOnExit>
          <RanaAlert mt={4} color="brand.deep_sea" status="warning">
            <FormattedMessage
              id="file.raster.unit_warning"
              defaultMessage="Changing this does not convert the data. Only the unit behind the numbers is changed."
            />
          </RanaAlert>
        </Collapse>
      </Box>

      {isCustomModalOpen && (
        <RanaForm initialValues={intialValues} doSubmit={applyCustomUnit}>
          <RanaModal
            isOpen={isCustomModalOpen}
            onClose={closeCustomModal}
            topBar={<FormattedMessage id="file.raster.custom_unit" defaultMessage="Custom unit" />}
            buttons={[
              <RanaForm.SubmitButton ml="auto" key="submit">
                <FormattedMessage id="file.raster.apply" defaultMessage="Apply" />
              </RanaForm.SubmitButton>,
            ]}
          >
            <RanaForm.Input
              isRequired
              field="code"
              label={intl.formatMessage({ id: "file.raster.code", defaultMessage: "Code" })}
              autoFocus
            />
            <RanaForm.Input
              field="label"
              label={intl.formatMessage({ id: "file.raster.label", defaultMessage: "Label" })}
            />
            <RanaForm.Input
              field="symbol"
              label={intl.formatMessage({ id: "file.raster.symbol", defaultMessage: "Symbol" })}
            />
            <RanaForm.Errors />
          </RanaModal>
        </RanaForm>
      )}
    </>
  );
};
